import {
  CommonPlateformXDialog,
  Loader,
  ShowToastError,
  ShowToastSuccess,
} from "@platformx/utilities";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  calculateInitialValues,
  calculateInitialValuesWithData,
  createContentType,
  fetchSchema,
  fetchSchemaWithData,
  publishContentType,
  updateContentType,
} from "../utils/helperFns";
import { DynamicComponent } from "./DynamicComponent/DynamicComponent";

export const DynamicComponentContainer = () => {
  const { documentId } = useParams();
  const searchPageUrl = new URL(window.location.href);
  const subCategory: string = searchPageUrl?.pathname?.split("/")?.[5];
  const category: string = searchPageUrl?.pathname?.split("/")?.[4];
  const [template, setTemplate] = useState<any>({});
  const [contentInstance, setContentInstance] = useState<any>({});
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const savedData = useRef({ id: "", name: "" });

  //Publish Success Dialog Close Handle
  const handleCloseDialog = () => {
    setShowPublishConfirm(false);
  };

  //saveData
  const saveContent = async (status) => {
    setIsLoading(true);
    try {
      const detailsRes: any = await createContentType(
        contentInstance,
        subCategory,
        template?.schema_document_type,
      );
      const { message, id, name } = detailsRes?.authoring_saveSchemaContent || {};
      if (message === "Successfully created!!!") {
        //ask statuscodes instead of this
        setIsLoading(false);
        setIsDraft(false);
        savedData.current = { id, name };
        if (status === "save") {
          ShowToastSuccess(`${subCategory} ${t("created_toast")}`);
        } else {
          try {
            setIsLoading(true);
            const response: any = await publishContentType(
              savedData,
              subCategory,
              template?.schema_document_type,
            );
            if (response?.authoring_publishSchemaContent?.message === "Published successfully!!!") {
              setIsLoading(false);
              setShowPublishConfirm(true);
            }
          } catch (err: any) {
            setIsLoading(false);
            ShowToastError(
              err.graphQLErrors.length > 0 ? err.graphQLErrors[0].message : t("api_error_toast"),
            );
          }
        }
      } else {
        ShowToastSuccess(detailsRes.authoring_createContent.message);
      }
    } catch (err: any) {
      setIsLoading(false);
      ShowToastError(t("api_error_toast"));
    }
  };

  //updateData
  const updateContent = async (status) => {
    setIsLoading(true);
    try {
      const detailsRes: any = await updateContentType(
        savedData,
        contentInstance,
        subCategory,
        template?.schema_document_type,
      );
      const { message } = detailsRes?.authoring_updateSchemaContent || {};
      if (message === "Successfully updated!!!") {
        //ask statuscodes instead of this
        setIsLoading(false);
        setIsDraft(false);
        if (status === "update") {
          // const displayName = getDisplayContentType(subCategory);
          ShowToastSuccess(`${template?.title} ${t("updated_toast")}`);
        } else {
          try {
            setIsLoading(true);
            const response: any = await publishContentType(
              savedData,
              subCategory,
              template?.schema_document_type,
            );
            if (response?.authoring_publishSchemaContent?.message === "Published successfully!!!") {
              setIsLoading(false);
              setShowPublishConfirm(true);
            }
          } catch (err: any) {
            setIsLoading(false);
            ShowToastError(
              err.graphQLErrors.length > 0 ? err.graphQLErrors[0].message : t("api_error_toast"),
            );
          }
        }
      } else {
        ShowToastSuccess(detailsRes.authoring_createContent.message);
      }
    } catch (err: any) {
      setIsLoading(false);
      ShowToastError(t("api_error_toast"));
    }
  };

  const handlePublish = () => {
    const { id } = savedData.current;
    if (isDraft) {
      //if any changes available
      if (documentId || id) {
        //if already saved
        updateContent("publish");
      } else {
        saveContent("publish");
      }
    } else {
      // publishContent();
      updateContent("publish");
    }
  };

  //GetSchema API
  const getSchema = async () => {
    try {
      // setIsLoading(true);
      setIsLoadingData(true);
      const detailsRes: any = await fetchSchema(subCategory); //change useQuery check Charts
      const schema = detailsRes?.authoring_getSchema;
      return schema;
    } catch (err: any) {
      ShowToastError(t("api_error_toast"));
    }
  };

  //GetSchemaData API
  const getSchemaWithData = async (initialValues) => {
    try {
      setIsLoadingData(true);
      const detailsRes: any = await fetchSchemaWithData(subCategory, documentId, category);
      const schema = detailsRes?.authoring_getSchemaContent;
      // calculateInitialValuesWithData only copies keys the schema itself declares as
      // fields — when the schema has no declared slug field (the case the generic SEO
      // slug field targets), the real stored slug would otherwise be silently dropped.
      const finalschema = {
        ...calculateInitialValuesWithData(initialValues, schema),
        slug: schema?.slug,
      };
      setContentInstance(finalschema);
      savedData.current = { id: schema?.document_id, name: schema?.name };
    } catch (err: any) {
      ShowToastError(t("api_error_toast"));
    }
  };

  const getSchemaAndData = async () => {
    try {
      setIsLoading(true);
      setIsLoadingData(true);
      const schema = await getSchema();
      setTemplate(schema);
      if ((schema?.fields?.length ?? 0) > 0) {
        const initialValues = await calculateInitialValues(schema?.fields);
        if (documentId) {
          //On Edit Flow
          await getSchemaWithData(initialValues);
        } else {
          //On Create Flow
          setContentInstance({ ...initialValues });
        }
      } else {
        ShowToastError(t("api_error_toast"));
        setIsLoading(false);
        setIsLoadingData(false);
      }
    } catch (err: any) {
      ShowToastError(t("api_error_toast"));
    } finally {
      setIsLoading(false);
      setIsLoadingData(false);
    }
  };

  //get Schema Details
  useEffect(() => {
    if (subCategory) {
      getSchemaAndData();
    }
  }, [subCategory]);
  return (
    <>
      {isLoading || isLoadingData ? <Loader /> : null}
      {!isLoadingData ? (
        <DynamicComponent
          contentType={subCategory}
          type={category}
          template={template}
          handleSave={updateContent}
          handlePublish={handlePublish}
          contentInstance={contentInstance}
          setContentInstance={setContentInstance}
          isEdit={documentId ? true : false}
          // setPageState={setPageState}
          listUrl={category}
        />
      ) : null}
      {showPublishConfirm ? (
        <CommonPlateformXDialog
          isDialogOpen={showPublishConfirm}
          title={t("congratulations")}
          subTitle={
            showPublishConfirm
              ? `${t("your")} ${template?.title} ${t("publish_popup_message")}`
              : t("requested_action")
          }
          closeButtonHandle={handleCloseDialog}
          confirmButtonText={t("go_to_listing")}
          confirmButtonHandle={() => navigate(`/${category}/${subCategory}`)}
          modalType='publish'
        />
      ) : null}
    </>
  );
};
